import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Toast, Popconfirm } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ObjSelector from '../../components/ObjSelector';

const UserAdmin = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [addFormApi, setAddFormApi] = useState(null);
    const [editFormApi, setEditFormApi] = useState(null);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/sp/list_sp_user_bind/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/sp/add_sp_user_bind/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/sp/edit_sp_user_bind/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/sp/delete_sp_user_bind/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp || timestamp == '0') return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '用户昵称',
            dataIndex: ['su', 'name'],
            width: 120,
            render: (text, record) => record.su?.name || '-',
        },
        {
            title: '子账号',
            dataIndex: ['subsp', 'name'],
            width: 120,
            render: (text, record) => record.subsp_uid == 0 ? '超级管理员' : (record.subsp?.name || '-'),
        },
        {
            title: '绑定时间',
            dataIndex: 'create_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该绑定吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.userAdminContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索用户UID/子账号UID/昵称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增绑定</Button>
                    {selectedRowKeys.length > 0 && (
                        <Popconfirm
                            title={`确定要删除选中的 ${selectedRowKeys.length} 个绑定吗？`}
                            onConfirm={() => handleDelete(selectedRowKeys)}
                        >
                            <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增管理员绑定"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
            >
                <Form
                    getFormApi={setAddFormApi}
                    onSubmit={values => {
                        if (!values.su_uid) {
                            Toast.error('请选择用户');
                            return;
                        }
                        handleAdd(values);
                    }}
                    autoComplete="off"
                >
                    <Form.Slot label="用户">
                        <ObjSelector
                            obj_type={0}
                            value={addFormApi?.getValue('su_uid')}
                            onChange={v => addFormApi?.setValue('su_uid', v)}
                            placeholder="请选择用户"
                            single
                        />
                    </Form.Slot>
                    <Form.Slot label="子账号">
                        <ObjSelector
                            obj_type={2}
                            value={addFormApi?.getValue('subsp_uid')}
                            onChange={v => addFormApi?.setValue('subsp_uid', v)}
                            placeholder="可不选，留空为超级管理员"
                            single
                        />
                    </Form.Slot>
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowAddModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="编辑管理员绑定"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
            >
                <Form
                    getFormApi={setEditFormApi}
                    initValues={{
                        su_uid: currentRecord?.su_uid,
                        subsp_uid: currentRecord?.subsp_uid
                    }}
                    onSubmit={values => {
                        if (!values.su_uid) {
                            Toast.error('请选择用户');
                            return;
                        }
                        handleEdit(values);
                    }}
                >
                    <Form.Slot label="用户">
                        <ObjSelector
                            obj_type={0}
                            value={editFormApi?.getValue('su_uid')}
                            onChange={v => editFormApi?.setValue('su_uid', v)}
                            placeholder="请选择用户"
                            single
                        />
                    </Form.Slot>
                    <Form.Slot label="子账号">
                        <ObjSelector
                            obj_type={2}
                            value={editFormApi?.getValue('subsp_uid')}
                            onChange={v => editFormApi?.setValue('subsp_uid', v)}
                            placeholder="可不选，留空为超级管理员"
                            single
                        />
                    </Form.Slot>
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default UserAdmin; 